// Global Instructions Rule Applied!
//
// Phase 3 — Recipients tab inside the email-campaign compose form.
//
// Structure:
//   Header row: count chips + "Pick recipients" button
//   Staged grid (Table over view__email_recipients) with per-row "Override"
//     action that opens RecipientOverrideDrawer.
//   PickRecipientsDrawer: distribution-list-driven resolve UX (a compact
//     version of SendCommunicationPage adapted to the drawer surface),
//     ending with "Stage". Shared resolve helpers
//     (frontend/src/core/utils/distributionListResolveUtils.ts) feed the
//     accounts→contacts tree, RFC-5322 formatting, lowercased-email
//     dedupe; no logic duplicated with the send-communication screen.
//
// The compose tab persists the campaign row; the Recipients tab persists
// rows in public.email_recipients. Both shape the campaign for the Phase 4
// queue/dispatch; this tab does not send.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  ThunderboltOutlined,
  SyncOutlined,
  EditOutlined,
  DeleteOutlined,
  UndoOutlined
} from '@ant-design/icons'
import MergeFieldsButton from './MergeFieldsButton.tsx'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type {
  EmailRecipientGridModel,
  EmailRecipientStageItem
} from '../../../state_manager/data_models/email_recipient_grid_model.ts'
import type { DistributionListRecipient } from '../../../state_manager/data_models/distribution_list_recipients_model.ts'
import { useEmailRecipients } from '../hooks/useEmailRecipients.ts'
import {
  contactName,
  pairKey,
  hasEmail,
  buildAccountTree,
  type AccountNode,
  type ContactNode,
  type ResolveGridRow
} from '../../../core/utils/distributionListResolveUtils.ts'
import RichTextEditor from '../../../core/components/RichTextEditor.jsx'
import type { CKEditorRichTextHandle } from '../../../core/components/CKEditorRichText.tsx'
import { useEditorEngine, useEditorConfigReady } from '../../global_configs/hooks/useEditorConfig.ts'
import { ShadowRichHtml } from '../../../core/components/ShadowRichHtml.tsx'
import TeamDetailDrawer from './TeamDetailDrawer.tsx'
import type { ReadOnlyFile } from '../../../core/components/ReadOnlyFileList.tsx'
import type { EmailCampaignAccountFilesState } from '../hooks/useEmailCampaignAccountFiles.ts'

const { Text, Title } = Typography

// Lazy-loaded (code-split; editor build loads from the CDN). Mirrors Full.tsx.
const CKEditorRichText = React.lazy(
  () => import('../../../core/components/CKEditorRichText.tsx')
)

const ROLE_OPTIONS = [
  { value: 'to', label: 'To' },
  { value: 'cc', label: 'Cc' }
]

// Recipient status → Tag colour (design-pattern Tag vocabulary: pending
// blue, done green, needs-attention gold, failure red). The failure-side
// statuses match finalize_email_campaign's failed rollup.
const RECIPIENT_STATUS_COLOR: Record<string, string> = {
  queued:       'blue',
  sending:      'processing',
  sent:         'green',
  delivered:    'green',
  failed:       'red',
  bounced:      'red',
  suppressed:   'gold',
  unsubscribed: 'gold',
  cancelled:    'default'
}

// Status Tag with the recipient's dispatch / delivery error in its Tooltip —
// the only place the error (e.g. "Dispatch stalled; marked failed by
// sweep …" or Postmark's rejection text) reaches the UI.
// How many account names a sentence lists before "and n more" (the queue
// gate's own refusal does the same).
const NAMED_ACCOUNTS = 5

const nameAccounts = (names: string[]): string =>
  names.length <= NAMED_ACCOUNTS
    ? names.join(', ')
    : `${names.slice(0, NAMED_ACCOUNTS).join(', ')} and ${names.length - NAMED_ACCOUNTS} more`

const RecipientStatusTag = ({ status, error }: { status: string; error?: string | null }) => {
  const tag = <Tag color={RECIPIENT_STATUS_COLOR[status] ?? 'default'}>{status}</Tag>
  return error ? <Tooltip title={error}>{tag}</Tooltip> : tag
}

interface SendFormValues {
  list_id?: string
  topic_id?: string
  roles_limit: string[]
  funds_limit: string[]
  lp_status_limit: string[]
  accounts_limit: string[]
  accounts_add: string[]
  contacts_add: string[]
}

const EMPTY_FORM: SendFormValues = {
  list_id: undefined,
  topic_id: undefined,
  roles_limit: [],
  funds_limit: [],
  lp_status_limit: [],
  accounts_limit: [],
  accounts_add: [],
  contacts_add: []
}

interface RecipientsTabProps {
  campaignId: string | null
  // The campaign's chosen body (Compose tab). Drives the merged preview in
  // the override drawer when the recipient has no body_override of its own.
  defaultBodyHtml: string
  defaultSubject: string
  // Phase 4b: the compose form's current send_mode. Team mode sends ONE
  // message per account (To: + Cc: from the group's recipients) using the
  // campaign-level subject + body — per-recipient overrides are ignored
  // by the dispatch. The Recipients tab uses this to (a) banner-explain
  // the team-mode contract and (b) disable the per-row Override action
  // so the user doesn't waste time editing values that won't ship.
  sendMode?: 'individual' | 'team'
  // Phase 4b layout: parent (Full.tsx) lifts the selected-row state so a
  // side-by-side PreviewPane can render against it. Null = nothing
  // selected → preview shows its empty state. Fired on row click.
  onSelectionChange?: (sel:
    | null
    | { kind: 'individual'; recipient: EmailRecipientGridModel }
    | { kind: 'team'; accountId: string; accountName: string; accountTeamName: string; recipients: EmailRecipientGridModel[] }
  ) => void
  selectedRecipientId?: string | null
  selectedAccountId?:   string | null
  // Phase 4b — parent (Full.tsx) caches recipient_count from the campaign
  // detail fetch to gate the Queue button's enabled state. The stage RPC
  // owns the persisted count, but RecipientsTab is where the mutation
  // happens, so notify the parent on successful stage so the Queue
  // button enables in place (without a navigate-away-and-back refetch).
  onStaged?: (count: number) => void
  // Per-account attachments (capital call notices, stage 5), read by the
  // parent so the compose card and the queue confirm share the one fetch.
  // The tab shows which accounts have their file and refetches after the
  // staged recipients change - who lacks a file depends on who is staged.
  accountFiles?: EmailCampaignAccountFilesState
  // Notice email (capital call notices, stage 7) or statement email
  // (investor statements, A3.14 stage 6): set for a campaign created by
  // "Send notices" on a capital call or "Send statements" on a statement
  // run. Its recipients and per-account attachments come from that record,
  // so the picker gives way to "Refresh from capital call" / "Refresh from
  // statement run" - a re-stage through the picker would replace every row
  // and lose the merge fields. The parent runs the refresh (and says how it
  // went); the tab re-reads the staged rows afterwards. `locked`: the email
  // is no longer a draft. `source` picks the wording (capital call when
  // absent).
  noticeEmail?: {
    locked: boolean
    onRefresh: () => Promise<void>
    source?: 'capital_call' | 'statement_run'
  } | null
}

export default function RecipientsTab({
  campaignId,
  defaultBodyHtml,
  defaultSubject,
  sendMode = 'individual',
  onSelectionChange,
  selectedRecipientId,
  selectedAccountId,
  onStaged,
  accountFiles,
  noticeEmail
}: RecipientsTabProps) {
  const isTeamMode = sendMode === 'team'
  const {
    savedLists,
    listsLoading,
    options,
    optionsLoading,
    resolved,
    resolving,
    resolve,
    clearResolved,
    loadListDefinition,
    staged,
    stagedLoading,
    stagedTotal,
    refetchStaged,
    stage,
    staging,
    savePerRecipientOverride,
    // Phase 4b
    savePerTeamOverride,
    addRecipientToTeam,
    removeRecipientRow,
    searchCandidates
  } = useEmailRecipients({ campaignId })

  // Phase 4b — team-detail drawer state.
  const [teamDrawerAccountId, setTeamDrawerAccountId] = useState<string | null>(null)

  const fromStatementRun = noticeEmail?.source === 'statement_run'
  const sourceNoun = fromStatementRun ? 'statement run' : 'capital call'
  const refreshLabel = `Refresh from ${sourceNoun}`
  const [refreshingFromCall, setRefreshingFromCall] = useState(false)
  const handleRefreshFromCall = useCallback(async () => {
    if (!noticeEmail) return
    setRefreshingFromCall(true)
    try {
      await noticeEmail.onRefresh()
      await refetchStaged()
    } finally {
      setRefreshingFromCall(false)
    }
  }, [noticeEmail, refetchStaged])

  // ── Per-account attachments ────────────────────────────────────────────
  const carriesFiles = accountFiles?.carriesFiles ?? false
  const filesByAccountId = accountFiles?.byAccountId
  const filesSummary = accountFiles?.summary ?? null
  const refetchAccountFiles = accountFiles?.refetch

  // Every mutation of the staged list ends in a refetch of `staged`; follow
  // it. The mount-time empty array is skipped (the parent's hook loads on
  // its own).
  const initialStagedRef = useRef(staged)
  useEffect(() => {
    if (staged === initialStagedRef.current) return
    refetchAccountFiles?.()
  }, [staged, refetchAccountFiles])

  /** The Attachments cell of one staged account (null = no account). */
  const attachmentsCell = useCallback(
    (accountId: string | null) => {
      if (!accountId) return <Tag color='gold'>No account</Tag>
      const count = filesByAccountId?.get(accountId)?.prog__file_count ?? 0
      return count > 0 ? (
        <Tag color='blue'>
          {count} file{count === 1 ? '' : 's'}
        </Tag>
      ) : (
        <Tag color='gold'>Missing</Tag>
      )
    },
    [filesByAccountId]
  )

  // Pick-recipients drawer state
  const [pickOpen, setPickOpen] = useState(false)
  const [form] = Form.useForm<SendFormValues>()
  const [listLoading, setListLoading] = useState(false)
  // Session-only exclusions inside the drawer (cleared on re-resolve).
  const [excludedAccounts, setExcludedAccounts] = useState<Set<string>>(new Set())
  const [excludedPairs, setExcludedPairs] = useState<Set<string>>(new Set())
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  // Per-row role overrides (drawer-local). Maps pairKey → 'to'|'cc'; absent
  // = use the role from the resolve row.
  const [roleOverrides, setRoleOverrides] = useState<Record<string, 'to' | 'cc'>>({})

  // Per-recipient override drawer state
  const [overrideRow, setOverrideRow] = useState<EmailRecipientGridModel | null>(null)

  const topicOptions = options?.topic ?? []
  const roleOpts = options?.roles ?? []
  const fundOpts = options?.funds ?? []
  const lpStatusOpts = options?.lp_status ?? []
  const accountOpts = options?.accounts ?? []
  const contactOpts = options?.contacts ?? []

  const listOptions = useMemo(
    () =>
      savedLists.map((l) => ({
        value: l.distribution_lists__id,
        label: l.distribution_lists__name
      })),
    [savedLists]
  )

  const onPickList = useCallback(
    async (listId: string | undefined) => {
      form.setFieldsValue({ list_id: listId })
      clearResolved()
      if (!listId) return
      setListLoading(true)
      try {
        const d = await loadListDefinition(listId)
        if (!d) {
          showErrorToast('Could not load that distribution list.')
          return
        }
        form.setFieldsValue({
          topic_id: d.distribution_lists__topic_id,
          roles_limit: d.distribution_lists__role_ids ?? [],
          funds_limit: d.distribution_lists__fund_ids ?? [],
          lp_status_limit: d.distribution_lists__lp_status_ids ?? [],
          accounts_limit: [],
          accounts_add: d.distribution_lists__account_ids ?? [],
          contacts_add: d.distribution_lists__contact_ids ?? []
        })
      } finally {
        setListLoading(false)
      }
    },
    [form, loadListDefinition, clearResolved]
  )

  const handleResolve = useCallback(async () => {
    let values: SendFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    const err = await resolve({
      topic_id: values.topic_id as string,
      roles_limit: values.roles_limit ?? [],
      funds_limit: values.funds_limit ?? [],
      accounts_limit: values.accounts_limit ?? [],
      lp_status_limit: values.lp_status_limit ?? [],
      accounts_add: values.accounts_add ?? [],
      contacts_add: values.contacts_add ?? []
    })
    setExcludedAccounts(new Set())
    setExcludedPairs(new Set())
    setExpandedKeys([])
    setRoleOverrides({})
    if (err) showErrorToast(err)
  }, [form, resolve])

  const tree: AccountNode[] = useMemo(() => buildAccountTree(resolved), [resolved])
  const allAccountKeys = useMemo(() => tree.map((n) => n.key), [tree])
  const allExpanded = tree.length > 0 && expandedKeys.length >= tree.length
  const anyExpanded = expandedKeys.length > 0

  const isIncluded = useCallback(
    (r: DistributionListRecipient): boolean =>
      hasEmail(r) && !excludedAccounts.has(r.accounts__id) && !excludedPairs.has(pairKey(r)),
    [excludedAccounts, excludedPairs]
  )

  const toggleAccount = useCallback((accountId: string, checked: boolean) => {
    setExcludedAccounts((prev) => {
      const next = new Set(prev)
      if (checked) next.delete(accountId)
      else next.add(accountId)
      return next
    })
  }, [])

  const toggleContact = useCallback((r: DistributionListRecipient, checked: boolean) => {
    setExcludedPairs((prev) => {
      const next = new Set(prev)
      if (checked) next.delete(pairKey(r))
      else next.add(pairKey(r))
      return next
    })
  }, [])

  const setRowRole = useCallback((r: DistributionListRecipient, role: 'to' | 'cc') => {
    setRoleOverrides((prev) => ({ ...prev, [pairKey(r)]: role }))
  }, [])

  const effectiveRole = useCallback(
    (r: DistributionListRecipient): 'to' | 'cc' => {
      const override = roleOverrides[pairKey(r)]
      if (override) return override
      const resolveRole = (r.role ?? 'to') as 'to' | 'cc'
      return resolveRole === 'cc' ? 'cc' : 'to'
    },
    [roleOverrides]
  )

  const included = useMemo(
    () => (resolved ?? []).filter(isIncluded),
    [resolved, isIncluded]
  )

  const handleStage = useCallback(async () => {
    if (included.length === 0) {
      toast.info('No included recipients to stage.')
      return
    }
    const items: EmailRecipientStageItem[] = included.map((r) => ({
      account_id: r.accounts__id ?? null,
      contact_id: r.contacts__id ?? null,
      email_address: (r.contacts__email || '').trim(),
      role: effectiveRole(r),
      // Empty merge_data here means "let the backend compute from the
      // template's merge_fields"; the backend synthesizes a resolve-row
      // shape from the flat fields below.
      merge_data: {
        first_name: r.contacts__first_name ?? '',
        last_name: r.contacts__last_name ?? '',
        email: r.contacts__email ?? '',
        account_name: r.accounts__name ?? ''
      }
    }))

    const values = form.getFieldsValue()
    const resolutionParams = {
      topic_id: values.topic_id,
      roles_limit: values.roles_limit ?? [],
      funds_limit: values.funds_limit ?? [],
      lp_status_limit: values.lp_status_limit ?? [],
      accounts_limit: values.accounts_limit ?? [],
      accounts_add: values.accounts_add ?? [],
      contacts_add: values.contacts_add ?? []
    }

    const err = await stage(items, resolutionParams)
    if (err) {
      showErrorToast(err)
      return
    }
    toast.success(`Staged ${items.length} recipient${items.length === 1 ? '' : 's'}`)
    onStaged?.(items.length)
    setPickOpen(false)
  }, [included, effectiveRole, form, stage, onStaged])

  const closePicker = useCallback(() => {
    if (resolving || staging) return
    setPickOpen(false)
  }, [resolving, staging])

  // ── Staged grid columns ────────────────────────────────────────────────

  const stagedColumns: ColumnsType<EmailRecipientGridModel> = useMemo(
    () => [
      {
        title: 'Account',
        dataIndex: 'email_recipients__account_name',
        key: 'email_recipients__account_name',
        render: (name: string) => name || <Text type='secondary'>—</Text>
      },
      {
        title: 'Contact',
        key: 'contact',
        render: (_: unknown, r: EmailRecipientGridModel) => {
          const full = `${r.email_recipients__contact_first_name ?? ''} ${
            r.email_recipients__contact_last_name ?? ''
          }`.trim()
          return full || <Text type='secondary'>—</Text>
        }
      },
      {
        title: 'Email',
        dataIndex: 'email_recipients__email_address',
        key: 'email_recipients__email_address',
        width: 240
      },
      ...(carriesFiles
        ? [
            {
              title: 'Attachments',
              key: 'attachments',
              width: 120,
              render: (_: unknown, r: EmailRecipientGridModel) =>
                attachmentsCell(r.email_recipients__account_id || null)
            }
          ]
        : []),
      {
        title: 'Role',
        dataIndex: 'email_recipients__role',
        key: 'email_recipients__role',
        width: 90,
        align: 'center',
        render: (role: string, r: EmailRecipientGridModel) => (
          <Select
            size='small'
            value={role}
            options={ROLE_OPTIONS}
            style={{ width: 70 }}
            onChange={async (v) => {
              const err = await savePerRecipientOverride(
                r.email_recipients__id,
                { role: v as 'to' | 'cc' }
              )
              if (err) showErrorToast(err)
              else toast.success(`Role updated to ${v.toUpperCase()}`)
            }}
          />
        )
      },
      {
        title: 'Override',
        key: 'override',
        width: 130,
        align: 'center',
        render: (_: unknown, r: EmailRecipientGridModel) => {
          // In team mode the dispatch ignores body_override_html /
          // subject_override entirely — so "Custom" would be a lie.
          if (isTeamMode) return <Text type='secondary'>n/a</Text>
          const any = r.email_recipients__has_body_override || r.email_recipients__has_subject_override
          return any ? (
            <Tag color='blue'>Custom</Tag>
          ) : (
            <Text type='secondary'>—</Text>
          )
        }
      },
      {
        title: 'Status',
        dataIndex: 'email_recipients__status',
        key: 'email_recipients__status',
        width: 100,
        render: (s: string, r: EmailRecipientGridModel) => (
          <RecipientStatusTag status={s} error={r.email_recipients__error} />
        )
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        align: 'center',
        render: (_: unknown, r: EmailRecipientGridModel) => (
          <Space size='small'>
            <Tooltip
              title={
                isTeamMode
                  ? 'Per-recipient overrides aren’t sent in team mode — each team gets one message using the campaign-level subject and body.'
                  : 'Edit override'
              }
            >
              {/* Tooltip needs a non-disabled child to fire on disabled buttons;
                  the inner Button receives the disabled prop. */}
              <Button
                size='small'
                type='text'
                icon={<EditOutlined />}
                disabled={isTeamMode}
                onClick={() => setOverrideRow(r)}
              />
            </Tooltip>
          </Space>
        )
      }
    ],
    [savePerRecipientOverride, isTeamMode, carriesFiles, attachmentsCell]
  )

  // ── Team-mode grid (Phase 4b) ──────────────────────────────────────────
  //
  // Client-side group of the per-recipient list into one row per account.
  // The team_name shown comes from the recipient row (we added
  // accounts__team_name in Phase 4b's view__email_recipients) — falls
  // back to the account name if blank, matching the dispatch's resolver.
  interface TeamRow {
    accountId:       string
    accountName:     string
    teamName:        string                          // accounts.team_name or '' (blank)
    recipients:      EmailRecipientGridModel[]
    recipientCount:  number
    toCount:         number
    ccCount:         number
    hasOverride:     boolean                          // any row in team has override
    // For status display: simplified — "queued" if all queued, otherwise
    // a representative status. The compose-tab status banner handles the
    // campaign-level rollups; this column is a glance, not a source of
    // truth. An errored row wins, so a failed team never reads as sent.
    representativeStatus: string
    representativeError:  string | null
  }

  const teamRows: TeamRow[] = useMemo(() => {
    if (!isTeamMode) return []
    const byAccount = new Map<string, EmailRecipientGridModel[]>()
    for (const r of staged) {
      const key = r.email_recipients__account_id || '__null__'
      const list = byAccount.get(key) ?? []
      list.push(r)
      byAccount.set(key, list)
    }
    const rows: TeamRow[] = []
    byAccount.forEach((rs, key) => {
      const first = rs[0]
      const errored = rs.find((x) => !!x.email_recipients__error)
      rows.push({
        accountId:      key,
        accountName:    first?.email_recipients__account_name || '(no account)',
        teamName:       (first?.email_recipients__account_team_name ?? '').trim(),
        recipients:     rs,
        recipientCount: rs.length,
        toCount:        rs.filter((x) => x.email_recipients__role === 'to').length,
        ccCount:        rs.filter((x) => x.email_recipients__role === 'cc').length,
        hasOverride:    rs.some(
          (x) =>
            x.email_recipients__has_body_override || x.email_recipients__has_subject_override
        ),
        representativeStatus:
          errored?.email_recipients__status ||
          rs.find((x) => x.email_recipients__status !== 'queued')?.email_recipients__status ||
          rs[0]?.email_recipients__status || 'queued',
        representativeError: errored?.email_recipients__error ?? null
      })
    })
    rows.sort((a, b) => a.accountName.localeCompare(b.accountName))
    return rows
  }, [isTeamMode, staged])

  const teamColumns: ColumnsType<TeamRow> = useMemo(
    () => [
      {
        title: 'Account',
        dataIndex: 'accountName',
        key: 'accountName',
        render: (_: unknown, t: TeamRow) => <Text strong>{t.accountName}</Text>
      },
      {
        title: 'Recipients',
        key: 'count',
        width: 130,
        render: (_: unknown, t: TeamRow) => (
          <Space size={4}>
            <Tag color={t.recipientCount > 0 ? 'green' : 'default'}>{t.recipientCount}</Tag>
            <Text type='secondary' style={{ fontSize: 12 }}>
              ({t.toCount} To / {t.ccCount} Cc)
            </Text>
          </Space>
        )
      },
      ...(carriesFiles
        ? [
            {
              title: 'Attachments',
              key: 'attachments',
              width: 120,
              render: (_: unknown, t: TeamRow) =>
                attachmentsCell(t.accountId === '__null__' ? null : t.accountId)
            }
          ]
        : []),
      {
        title: 'Override',
        key: 'override',
        width: 110,
        align: 'center',
        render: (_: unknown, t: TeamRow) =>
          t.hasOverride ? <Tag color='blue'>Custom</Tag> : <Text type='secondary'>—</Text>
      },
      {
        title: 'Status',
        key: 'status',
        width: 110,
        render: (_: unknown, t: TeamRow) => (
          <RecipientStatusTag status={t.representativeStatus} error={t.representativeError} />
        )
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        align: 'center',
        render: (_: unknown, t: TeamRow) => (
          <Tooltip title='Manage team: To/Cc, add/remove recipients, per-team override'>
            <Button size='small' icon={<EditOutlined />} onClick={() => setTeamDrawerAccountId(t.accountId)}>
              Manage
            </Button>
          </Tooltip>
        )
      }
    ],
    [carriesFiles, attachmentsCell]
  )

  // The open team's own files, for the drawer (undefined = the campaign
  // carries no per-account attachments, so the drawer shows no card).
  const teamDrawerFiles: ReadOnlyFile[] | undefined = useMemo(() => {
    if (!carriesFiles || !teamDrawerAccountId) return undefined
    return (filesByAccountId?.get(teamDrawerAccountId)?.files ?? []).map((f) => ({
      id: f.file_urls_email_campaign_accounts__id,
      name: f.file_urls_email_campaign_accounts__file_name,
      path: f.file_urls_email_campaign_accounts__file_url,
      sizeBytes: f.file_urls_email_campaign_accounts__file_size_bytes
    }))
  }, [carriesFiles, teamDrawerAccountId, filesByAccountId])

  // What the queue would refuse, and what would go to nobody - said before
  // the user clicks Queue.
  const accountFileWarnings: string[] = useMemo(() => {
    if (!filesSummary) return []
    const lines: string[] = []
    if (carriesFiles && filesSummary.null_account_recipients > 0) {
      const n = filesSummary.null_account_recipients
      lines.push(
        `${n} recipient${n === 1 ? ' has' : 's have'} no account, so no attachment can be matched to ${n === 1 ? 'it' : 'them'}.`
      )
    }
    if (filesSummary.missing.length > 0) {
      lines.push(
        `No attachment for: ${nameAccounts(filesSummary.missing.map((a) => a.accounts__name))}. ` +
          'Every account on this campaign needs its own file before it can be queued.'
      )
    }
    if (filesSummary.unstaged.length > 0) {
      lines.push(
        `Attachments with no recipient: ${nameAccounts(filesSummary.unstaged.map((a) => a.accounts__name))}. ` +
          'These accounts are not staged, so their files will not be sent.'
      )
    }
    return lines
  }, [filesSummary, carriesFiles])

  const currentTeam: TeamRow | null = useMemo(
    () => teamRows.find((t) => t.accountId === teamDrawerAccountId) ?? null,
    [teamRows, teamDrawerAccountId]
  )

  // Keep the parent's lifted preview selection in sync with refetched
  // `staged`. A per-recipient or per-team override save/clear refetches the
  // grid (savePerRecipientOverride / savePerTeamOverride both await
  // fetchStaged), but Full lifts only a *snapshot* of the selected row into
  // previewSelection — so without re-emitting, the compose PreviewPane keeps
  // rendering the pre-clear override until the user reselects the row. Re-emit
  // the currently-selected row/team from the fresh rows whenever they change.
  useEffect(() => {
    if (!onSelectionChange) return
    if (!isTeamMode && selectedRecipientId) {
      const r = staged.find((x) => x.email_recipients__id === selectedRecipientId)
      if (r) onSelectionChange({ kind: 'individual', recipient: r })
    } else if (isTeamMode && selectedAccountId) {
      const t = teamRows.find((x) => x.accountId === selectedAccountId)
      if (t) {
        onSelectionChange({
          kind:            'team',
          accountId:       t.accountId,
          accountName:     t.accountName,
          accountTeamName: t.teamName,
          recipients:      t.recipients
        })
      }
    }
    // Re-sync only when the underlying rows change (i.e. after a refetch). The
    // selection ids + onSelectionChange are intentionally omitted: a row click
    // already emits, and including them would re-fire on every parent render
    // (onSelectionChange is a stable setState ref, so there is no loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staged, teamRows, isTeamMode])

  // ── Hook adapters for the drawer (capture the open accountId) ──────────

  const handleTeamRoleChange = useCallback(
    async (recipientId: string, role: 'to' | 'cc'): Promise<string | null> => {
      return savePerRecipientOverride(recipientId, { role })
    },
    [savePerRecipientOverride]
  )

  const handleTeamRemove = useCallback(
    async (recipientId: string): Promise<string | null> => {
      return removeRecipientRow(recipientId)
    },
    [removeRecipientRow]
  )

  const handleTeamAdd = useCallback(
    async (contactId: string, role: 'to' | 'cc'): Promise<string | null> => {
      if (!teamDrawerAccountId) return 'No team open'
      return addRecipientToTeam(teamDrawerAccountId, { contact_id: contactId, role })
    },
    [addRecipientToTeam, teamDrawerAccountId]
  )

  const handleTeamSaveOverride = useCallback(
    async (payload: { subject_override: string | null; body_override_html: string | null }): Promise<string | null> => {
      if (!teamDrawerAccountId) return 'No team open'
      return savePerTeamOverride(teamDrawerAccountId, payload)
    },
    [savePerTeamOverride, teamDrawerAccountId]
  )

  const handleTeamSearchCandidates = useCallback(
    async (term: string) => {
      if (!teamDrawerAccountId) return []
      return searchCandidates(teamDrawerAccountId, term || null)
    },
    [searchCandidates, teamDrawerAccountId]
  )

  // Early return AFTER all hooks have run unconditionally — hooks must be
  // called in the same order every render (rules-of-hooks).
  if (!campaignId) {
    return (
      <Card>
        <Text type='secondary'>
          Save the draft first to add recipients. Recipients are staged once
          the campaign row exists.
        </Text>
      </Card>
    )
  }

  return (
    <div style={{ padding: 4 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Tag color={stagedTotal > 0 ? 'green' : 'default'}>
          {stagedTotal} staged recipient{stagedTotal === 1 ? '' : 's'}
        </Tag>
        {noticeEmail ? (
          <Popconfirm
            title={`Refresh from the ${sourceNoun}?`}
            description={
              fromStatementRun
                ? 'The recipients are taken again from the recipients topic, any per-team wording is reset, and the attachments are replaced with the current issued statements of the same funds.'
                : 'The recipients are taken again from the Capital Calls topic, any per-team wording is reset, and the attachments are replaced with the current notices.'
            }
            okText='Refresh'
            disabled={noticeEmail.locked || refreshingFromCall}
            onConfirm={handleRefreshFromCall}
          >
            <Tooltip
              title={
                noticeEmail.locked
                  ? `Only a draft ${fromStatementRun ? 'statement' : 'notice'} email can be refreshed.`
                  : undefined
              }
            >
              <Button type='primary' icon={<SyncOutlined />} loading={refreshingFromCall} disabled={noticeEmail.locked}>
                {refreshLabel}
              </Button>
            </Tooltip>
          </Popconfirm>
        ) : (
          <Button
            type='primary'
            icon={<ThunderboltOutlined />}
            onClick={() => setPickOpen(true)}
          >
            Pick / amend recipients
          </Button>
        )}
        <Button onClick={() => refetchStaged()}>Refresh</Button>
      </Space>

      {accountFileWarnings.length > 0 ? (
        <Alert
          type='warning'
          showIcon
          style={{ marginBottom: 12 }}
          message='Per-account attachments'
          description={
            <div>
              {accountFileWarnings.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          }
        />
      ) : null}

      {isTeamMode ? (
        <Table<TeamRow>
          rowKey='accountId'
          columns={teamColumns}
          dataSource={teamRows}
          loading={stagedLoading}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          size='small'
          rowClassName={(t) =>
            selectedAccountId && t.accountId === selectedAccountId ? 'email-row-selected' : ''
          }
          locale={{
            emptyText:
              stagedTotal === 0 && !stagedLoading
                ? noticeEmail
                  ? `No teams staged yet. Click "${refreshLabel}" to take the recipients and ${fromStatementRun ? 'statements from the run' : 'notices from the call'}.`
                  : 'No teams staged yet. Click "Pick / amend recipients" to resolve a distribution list — accounts will appear here as teams.'
                : undefined
          }}
          onRow={(t) => ({
            onClick: () => {
              if (onSelectionChange) {
                onSelectionChange({
                  kind:             'team',
                  accountId:        t.accountId,
                  accountName:      t.accountName,
                  accountTeamName:  t.teamName,
                  recipients:       t.recipients
                })
              } else {
                // Fallback: open the manage drawer directly (legacy behaviour).
                setTeamDrawerAccountId(t.accountId)
              }
            },
            style: { cursor: 'pointer' }
          })}
        />
      ) : (
        <Table<EmailRecipientGridModel>
          rowKey='email_recipients__id'
          columns={stagedColumns}
          dataSource={staged}
          loading={stagedLoading}
          pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
          size='small'
          rowClassName={(r) =>
            selectedRecipientId && r.email_recipients__id === selectedRecipientId
              ? 'email-row-selected'
              : ''
          }
          locale={{
            emptyText:
              stagedTotal === 0 && !stagedLoading
                ? noticeEmail
                  ? `No recipients staged yet. Click "${refreshLabel}" to take the recipients and ${fromStatementRun ? 'statements from the run' : 'notices from the call'}.`
                  : 'No recipients staged yet. Click "Pick / amend recipients" to resolve from a distribution list.'
                : undefined
          }}
          onRow={(r) => ({
            onClick: () => {
              if (onSelectionChange) {
                onSelectionChange({ kind: 'individual', recipient: r })
              }
            },
            style: { cursor: 'pointer' }
          })}
        />
      )}

      {/* ── Team detail drawer (Phase 4b) ───────────────────────────────── */}
      <TeamDetailDrawer
        open={!!teamDrawerAccountId && isTeamMode}
        accountId={teamDrawerAccountId}
        accountName={currentTeam?.accountName ?? ''}
        accountTeamName={currentTeam?.teamName ?? ''}
        recipients={currentTeam?.recipients ?? []}
        campaignSubject={defaultSubject}
        campaignBodyHtml={defaultBodyHtml}
        locked={false}
        accountFiles={teamDrawerFiles}
        onRoleChange={handleTeamRoleChange}
        onRemove={handleTeamRemove}
        onAdd={handleTeamAdd}
        onSaveOverride={handleTeamSaveOverride}
        onSearchCandidates={handleTeamSearchCandidates}
        onClose={() => setTeamDrawerAccountId(null)}
      />

      {/* ── Pick-recipients drawer ────────────────────────────────────── */}
      <Drawer
        title='Pick recipients'
        placement='right'
        width={920}
        open={pickOpen}
        onClose={closePicker}
        maskClosable={!resolving && !staging}
        destroyOnClose
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={closePicker} disabled={resolving || staging}>
              Cancel
            </Button>
            <Button
              type='primary'
              icon={<ThunderboltOutlined />}
              loading={staging}
              disabled={included.length === 0}
              onClick={handleStage}
            >
              Stage {included.length || ''} recipient
              {included.length === 1 ? '' : 's'}
            </Button>
          </Space>
        }
      >
        <Spin spinning={listsLoading || optionsLoading || listLoading}>
          <Form form={form} layout='vertical' initialValues={EMPTY_FORM}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item name='list_id' label='Saved distribution list (optional)'>
                  <Select
                    showSearch
                    allowClear
                    placeholder='Pick a saved list…'
                    options={listOptions}
                    optionFilterProp='label'
                    onChange={(v) => onPickList(v)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name='topic_id'
                  label='Topic'
                  rules={[{ required: true, message: 'Topic is required' }]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder='Pick a topic'
                    options={topicOptions}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item name='roles_limit' label='Role (filter)'>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder='Any role'
                    options={roleOpts}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='funds_limit' label='Fund (filter)'>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder='Any fund'
                    options={fundOpts}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='lp_status_limit' label='LP status (filter)'>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder='Any LP status'
                    options={lpStatusOpts}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='accounts_limit' label='Account (filter)'>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder='Any account'
                    options={accountOpts}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='accounts_add' label='Force-include accounts'>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder='Pick accounts to add'
                    options={accountOpts}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name='contacts_add' label='Force-include contacts'>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder='Pick contacts to add'
                    options={contactOpts}
                    optionFilterProp='label'
                  />
                </Form.Item>
              </Col>
            </Row>

            <Space>
              <Button
                type='primary'
                icon={<ThunderboltOutlined />}
                loading={resolving}
                onClick={handleResolve}
              >
                Resolve recipients
              </Button>
              {resolved !== null && (
                <Text type='secondary'>
                  {included.length} included · {resolved.length - included.length} excluded
                </Text>
              )}
            </Space>
          </Form>

          {resolved !== null && (
            <div style={{ marginTop: 18 }}>
              <Table<ResolveGridRow>
                rowKey='key'
                dataSource={tree as unknown as ResolveGridRow[]}
                pagination={false}
                expandable={{
                  expandedRowKeys: expandedKeys,
                  onExpandedRowsChange: (keys) =>
                    setExpandedKeys(keys as string[])
                }}
                size='small'
                columns={[
                  {
                    title: (
                      <Space size={4}>
                        <Button
                          type='text'
                          size='small'
                          onClick={() => setExpandedKeys(allAccountKeys)}
                          disabled={tree.length === 0 || allExpanded}
                        >
                          +
                        </Button>
                        <Button
                          type='text'
                          size='small'
                          onClick={() => setExpandedKeys([])}
                          disabled={!anyExpanded}
                        >
                          −
                        </Button>
                        <span>Recipient</span>
                      </Space>
                    ),
                    key: 'recipient',
                    render: (_: unknown, row: ResolveGridRow) => {
                      if (row.isAccount) {
                        const n = row as AccountNode
                        return (
                          <Space>
                            <Text strong>{n.accountName}</Text>
                            <Tag color='geekblue'>
                              {n.contacts.length} contact{n.contacts.length === 1 ? '' : 's'}
                            </Tag>
                          </Space>
                        )
                      }
                      const r = (row as ContactNode).recipient
                      return <span>{contactName(r)}</span>
                    }
                  },
                  {
                    title: 'Email',
                    key: 'email',
                    width: 240,
                    render: (_: unknown, row: ResolveGridRow) =>
                      row.isAccount ? null : (
                        (row as ContactNode).recipient.contacts__email || (
                          <Text type='secondary'>—</Text>
                        )
                      )
                  },
                  {
                    title: 'Role',
                    key: 'role',
                    width: 100,
                    render: (_: unknown, row: ResolveGridRow) => {
                      if (row.isAccount) return null
                      const r = (row as ContactNode).recipient
                      if (!hasEmail(r)) return null
                      return (
                        <Select
                          size='small'
                          value={effectiveRole(r)}
                          options={ROLE_OPTIONS}
                          style={{ width: 70 }}
                          onChange={(v) => setRowRole(r, v as 'to' | 'cc')}
                        />
                      )
                    }
                  },
                  {
                    title: 'Include',
                    key: 'include',
                    width: 80,
                    align: 'center',
                    render: (_: unknown, row: ResolveGridRow) => {
                      if (row.isAccount) {
                        const n = row as AccountNode
                        const sendable = n.contacts.filter(hasEmail).length
                        const checked = !excludedAccounts.has(n.accountId)
                        return (
                          <Checkbox
                            checked={checked}
                            disabled={sendable === 0}
                            onChange={(e) => toggleAccount(n.accountId, e.target.checked)}
                          />
                        )
                      }
                      const r = (row as ContactNode).recipient
                      const noEmail = !hasEmail(r)
                      const accountExcluded = excludedAccounts.has(r.accounts__id)
                      return (
                        <Checkbox
                          disabled={noEmail || accountExcluded}
                          checked={!excludedPairs.has(pairKey(r))}
                          onChange={(e) => toggleContact(r, e.target.checked)}
                        />
                      )
                    }
                  }
                ]}
              />
            </div>
          )}
        </Spin>
      </Drawer>

      {/* ── Per-recipient override drawer ────────────────────────────────── */}
      <RecipientOverrideDrawer
        recipient={overrideRow}
        defaultBodyHtml={defaultBodyHtml}
        defaultSubject={defaultSubject}
        onClose={() => setOverrideRow(null)}
        onSave={savePerRecipientOverride}
      />
    </div>
  )
}

// ─── Per-recipient override drawer ─────────────────────────────────────────

interface OverrideDrawerProps {
  recipient: EmailRecipientGridModel | null
  defaultBodyHtml: string
  defaultSubject: string
  onClose: () => void
  onSave: (
    recipientId: string,
    payload: {
      subject_override?: string | null
      body_override_html?: string | null
      role?: 'to' | 'cc'
    }
  ) => Promise<string | null>
}

function RecipientOverrideDrawer({
  recipient,
  defaultBodyHtml,
  defaultSubject,
  onClose,
  onSave
}: OverrideDrawerProps) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  // Live mirror of the body for the preview pane — updated on every keystroke
  // (RichTextEditor.onLiveChange), whereas `body` (the save/value source) only
  // settles on blur. Without it the preview wouldn't move until the editor lost
  // focus.
  const [livePreviewBody, setLivePreviewBody] = useState('')
  const [saving, setSaving] = useState(false)
  // Editor engine (cfg-driven). 'ckeditor' swaps in CKEditor and inline-at-
  // saves the override body (it's the final sent body for this recipient).
  const editorEngine = useEditorEngine()
  // Gate the editor until globalConfigs loads (avoids a cold-load Jodit flash).
  const engineReady = useEditorConfigReady()
  const editorRef = useRef<CKEditorRichTextHandle | null>(null)
  // Context-aware modal (static Modal.* renders without the app's themed
  // ConfigProvider context — and can sit behind the Drawer — so the confirm
  // never reached the user). Mirrors the team drawer's pattern.
  const [modalApi, modalContextHolder] = Modal.useModal()

  // Substitute the recipient's merge_data into the HTML so the preview pane
  // shows the final, fully-merged content. Phase 4 will do this server-side
  // during render; here we mirror the substitution for an accurate preview.
  // Simple {{key}} replacement — keep in lockstep with the Phase 4 edge
  // function and the cfg.email_templates merge_fields schema (currently
  // {first_name, last_name, email, account_name}).
  const substituteMerge = useCallback(
    (html: string, mergeData: unknown): string => {
      if (!html) return ''
      const data =
        mergeData && typeof mergeData === 'object' && !Array.isArray(mergeData)
          ? (mergeData as Record<string, unknown>)
          : {}
      return html.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, key: string) => {
        const v = data[key]
        return typeof v === 'string' || typeof v === 'number' ? String(v) : ''
      })
    },
    []
  )

  useEffect(() => {
    if (!recipient) return
    // Pre-fill from the recipient's stored override when one exists, otherwise
    // from the campaign draft (subject + body) so the user edits *from* the
    // drafted email instead of a blank canvas. handleSave treats a value still
    // equal to the campaign default as "no override", so pre-filling never
    // silently freezes a copy for a recipient the user opens but doesn't edit.
    const initialBody = recipient.email_recipients__body_override_html ?? defaultBodyHtml
    setSubject(recipient.email_recipients__subject_override ?? defaultSubject)
    setBody(initialBody)
    setLivePreviewBody(initialBody)
  }, [recipient, defaultSubject, defaultBodyHtml])

  if (!recipient) return null

  const previewHtml = substituteMerge(
    livePreviewBody || defaultBodyHtml,
    recipient.email_recipients__merge_data
  )
  const previewSubject = substituteMerge(
    subject || defaultSubject,
    recipient.email_recipients__merge_data
  )

  const handleSave = async () => {
    setSaving(true)
    // The editor is pre-filled from the campaign default, so persist an
    // override only when the recipient's value genuinely diverges. An empty
    // string clears the column server-side (→ the recipient falls back to the
    // campaign subject/body); a real value is stored as the override. Sending
    // null would be a no-op and could strand a stale override on revert.
    // Inline-at-save (plan §6.1) only on the persist path: the override is the
    // final sent body for this recipient, so a CKEditor body must be baked
    // inline. An untouched body stays === the campaign default (CKEditor
    // doesn't fire change on load) → cleared, never re-inlined.
    let overrideBody = body === defaultBodyHtml ? '' : body
    if (overrideBody && editorEngine === 'ckeditor') {
      try {
        const inline = await editorRef.current?.getInlineHtml()
        if (inline && inline.trim()) overrideBody = inline
      } catch {
        /* keep the class-based body fallback */
      }
    }
    const err = await onSave(recipient.email_recipients__id, {
      subject_override: subject === defaultSubject ? '' : subject,
      body_override_html: overrideBody
    })
    setSaving(false)
    if (err) {
      showErrorToast(err)
      return
    }
    toast.success('Override saved')
    onClose()
  }

  const handleClear = () => {
    modalApi.confirm({
      title: 'Clear all overrides for this recipient?',
      content:
        'The recipient will receive the campaign defaults (subject + body) on send.',
      okText: 'Clear',
      okButtonProps: { danger: true },
      onOk: async () => {
        const err = await onSave(recipient.email_recipients__id, {
          // Empty string (not null) is what the backend treats as "clear" —
          // null means "leave the column untouched", so a clear must send ''.
          subject_override: '',
          body_override_html: ''
        })
        if (err) {
          showErrorToast(err)
          return
        }
        toast.success('Overrides cleared')
        // Reset the editor in place to the campaign defaults so the cleared
        // state is visible immediately and the drawer stays open (mirrors the
        // team drawer's Clear behaviour).
        setSubject(defaultSubject)
        setBody(defaultBodyHtml)
        setLivePreviewBody(defaultBodyHtml)
      }
    })
  }

  return (
    <Drawer
      title={`Override · ${recipient.email_recipients__email_address}`}
      placement='right'
      width={1080}
      open={!!recipient}
      onClose={onClose}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={handleClear} icon={<DeleteOutlined />} danger>
            Clear overrides
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='primary' onClick={handleSave} loading={saving}>
            Save
          </Button>
        </Space>
      }
    >
      {modalContextHolder}
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Title level={5}>Compose</Title>
          <Form layout='vertical'>
            <Form.Item
              label='Subject (override)'
              help='Pre-filled from the campaign subject. Edit to customise for this recipient; reset (or leave it unchanged) to keep using the campaign subject.'
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <Button
                  type='link'
                  size='small'
                  icon={<UndoOutlined />}
                  onClick={() => setSubject(defaultSubject)}
                  disabled={subject === defaultSubject}
                  style={{ padding: 0 }}
                >
                  Reset to campaign subject
                </Button>
              </div>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                allowClear
                placeholder={defaultSubject}
              />
            </Form.Item>
            <Form.Item
              label='Body (override)'
              help='Pre-filled from the campaign body. Edit to customise for this recipient; reset (or leave it unchanged) to keep using the campaign body. Sanitized server-side on save.'
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  marginBottom: 4
                }}
              >
                <MergeFieldsButton type='link' />
                <Button
                  type='link'
                  size='small'
                  icon={<UndoOutlined />}
                  onClick={() => {
                    setBody(defaultBodyHtml)
                    setLivePreviewBody(defaultBodyHtml)
                  }}
                  disabled={livePreviewBody === defaultBodyHtml}
                  style={{ padding: 0 }}
                >
                  Reset to campaign body
                </Button>
              </div>
              <div style={{ height: 380 }}>
                {!engineReady ? (
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Spin />
                  </div>
                ) : editorEngine === 'ckeditor' ? (
                  <React.Suspense
                    fallback={
                      <div style={{ padding: 24, textAlign: 'center' }}>
                        <Spin />
                      </div>
                    }
                  >
                    <CKEditorRichText
                      ref={editorRef}
                      value={body}
                      onChange={setBody}
                      onLiveChange={setLivePreviewBody}
                      placeholder='Empty — the campaign body will be used…'
                      imageUpload={false}
                    />
                  </React.Suspense>
                ) : (
                  <RichTextEditor
                    value={body}
                    onChange={setBody}
                    onLiveChange={setLivePreviewBody}
                    placeholder='Empty — the campaign body will be used…'
                  />
                )}
              </div>
            </Form.Item>
          </Form>
        </Col>
        <Col xs={24} md={12}>
          <Title level={5}>Preview (merged)</Title>
          <Card size='small' style={{ marginBottom: 8 }}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Subject:
            </Text>{' '}
            <Text>{previewSubject || '(empty)'}</Text>
          </Card>
          <ShadowRichHtml
            html={previewHtml || '<p style="color:#bfbfbf">(empty body)</p>'}
            className='block'
          />
        </Col>
      </Row>
    </Drawer>
  )
}
